import { Routes, Route, useLocation, useNavigationType, Navigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { lazyRetry } from './lib/retryLazy';
import { tickerReports } from '@/config/tickerReports';

import { AnimatePresence, motion } from 'framer-motion';
import { MainLayout, PlainLayout } from './layouts/LandingLayout';
import Loader from "./Loader"

// Eagerly loaded (always needed for public pages)
import ProtectedRoute from '@/components/ProtectedRoute';
import { UserProvider, useUser } from '@/context/UserContext';
import { DocsScrollProvider } from '@/contexts/DocsScrollContext';
import useUserProfile from '@/hooks/useUserProfile';

const HomePage = lazyRetry(() => import('./pages/HomePage'));
const AboutPage = lazyRetry(() => import('./pages/AboutPage'));
const ProductsPage = lazyRetry(() => import('./pages/ProductsPage'));
const DevelopersPage = lazyRetry(() => import('./pages/DevelopersPage'));
const ContactPage = lazyRetry(() => import('./pages/ContactPage'));
const PricingPage = lazyRetry(() => import('./pages/PricingPage'));
const FaqPage = lazyRetry(() => import('@/pages/FaqPage'));
const JobsPage = lazyRetry(() => import('@/pages/JobsPage'));
const PrivacyPage = lazyRetry(() => import('@/pages/PrivacyPage'));
const TermsPage = lazyRetry(() => import('@/pages/TermsPage'));
const UnsubscribePage = lazyRetry(() => import('@/pages/UnsubscribePage'));
const BlogPage = lazyRetry(() => import('@/pages/BlogPage'));
const BlogArticlePage = lazyRetry(() => import('@/pages/BlogArticlePage'));
const SitemapPage = lazyRetry(() => import('@/pages/SitemapPage'));
const ComparisonPage = lazyRetry(() => import('@/pages/compare/ComparisonPage'));
const CompareIndexPage = lazyRetry(() => import('@/pages/compare/CompareIndexPage'));
// const GrazingPage = lazyRetry(() => import('@/pages/Grazing'));

// Auth pages
const LoginPage = lazyRetry(() => import('@/pages/auth/LoginPage'));
const SignupPage = lazyRetry(() => import('@/pages/auth/SignupPage'));
const VerifyEmailPage = lazyRetry(() => import('@/pages/auth/VerifyEmailPage'));
const ForgotPasswordPage = lazyRetry(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazyRetry(() => import('@/pages/auth/ResetPasswordPage'));

// Product pages
const StocksPage = lazyRetry(() => import('./pages/products/StocksPage'));
const CreditPage = lazyRetry(() => import('./pages/products/CreditPage'));
const EsgPage = lazyRetry(() => import('./pages/products/EsgPage'));
const EtfsPage = lazyRetry(() => import('./pages/products/EtfsPage'));
const SupplyChainPage = lazyRetry(() => import('./pages/products/SupplyChainPage'));
const CryptoPage = lazyRetry(() => import('./pages/products/CryptoPage'));
const ForexPage = lazyRetry(() => import('./pages/products/ForexPage'));
const FuturesPage = lazyRetry(() => import('./pages/products/FuturesPage'));
const EconomicsPage = lazyRetry(() => import('@/pages/products/EconomicsPage'));
const IndicesPage = lazyRetry(() => import('@/pages/products/IndicesPage'));
const CorporateProfilesPage = lazyRetry(() => import('@/pages/products/CorporateProfilesPage'));
const SentimentPage = lazyRetry(() => import('@/pages/products/SentimentPage'));
const WebtrafficPage = lazyRetry(() => import('@/pages/products/WebtrafficPage'));
const NewsPage = lazyRetry(() => import('@/pages/products/NewsPage'));
const DisclosuresPage = lazyRetry(() => import('@/pages/products/DisclosuresPage'));
const EarningsPage = lazyRetry(() => import('@/pages/products/EarningsPage'));

// Developer subpages
const LibrariesPage = lazyRetry(() => import('./pages/LibrariesPage'));


const TickerReportPage = lazyRetry(() => import('./pages/TickerReportPage'));
const StocksHubPage = lazyRetry(() => import('./pages/StocksHubPage'));

// Dashboard components - ALL lazy now (no eager import!)
const DashboardLayout = lazyRetry(() => import('./layouts/DashboardLayout'));
const SearchModal = lazyRetry(() => import('@/components/dashboard/SearchModal'));
const DocPage = lazyRetry(() => import('@/pages/DocsPage'));
const General = lazyRetry(() => import('@/components/dashboard/General'));
const Usage = lazyRetry(() => import('@/components/dashboard/Usage'));
const ApiKeys = lazyRetry(() => import('@/components/dashboard/ApiKeys'));
const Billing = lazyRetry(() => import('@/components/dashboard/Billing'));
const Profile = lazyRetry(() => import('@/components/dashboard/Profile'));
const ServiceHealth = lazyRetry(() => import('@/components/dashboard/ServiceHealth'));
const Security = lazyRetry(() => import('@/components/dashboard/Security'));
const Playground = lazyRetry(() => import('@/components/dashboard/Playground'));
const MCPConfig = lazyRetry(() => import('@/components/dashboard/MCPConfig'));

let hasAnimatedOnce = false;

const pageVariants = {
  initial: { opacity: 0 },
  in: { opacity: 1 },
  out: { opacity: 0 },
};

const AnimatedPage = ({ children }: { children: React.ReactNode }) => {
  const isFirstRender = !hasAnimatedOnce;

  useEffect(() => {
    hasAnimatedOnce = true;
  }, []);

  return (
    <motion.div
      initial={isFirstRender ? false : "initial"}
      animate="in"
      exit="out"
      variants={pageVariants}
      // transition={{ duration: 0.2 }}
    >
        {children}
    </motion.div>
  );
};

const scrollPositions = new Map<string, number>();

function ScrollToTop() {
  const { pathname, key } = useLocation();
  const navigationType = useNavigationType();
  const initialRenderDone = useRef(false);

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    document.documentElement.style.overflowAnchor = 'none';
  }, []);

  useEffect(() => {
    if (pathname.includes('/docs')) return;

    const saveScroll = () => {
      scrollPositions.set(key, window.scrollY);
    };

    window.addEventListener('scroll', saveScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', saveScroll);
      saveScroll();
    };
  }, [key, pathname]);

  useEffect(() => {
    if (pathname.includes('/docs')) return;

    // On initial load (prerender or SPA), skip scroll-to-top.
    // Just record the starting position so POP (back/forward) can restore it.
    if (!initialRenderDone.current) {
      initialRenderDone.current = true;
      scrollPositions.set(key, window.scrollY);
      return;
    }

    const target = navigationType === 'POP'
      ? (scrollPositions.get(key) ?? 0)
      : 0;

    // The destination page (and its lazy chunks) may not be rendered yet when
    // this runs - AnimatePresence waits for the old page to exit first, and
    // React.lazy pages mount late. Re-apply the target scroll each frame until
    // the document has settled at a stable height, so we never land at a
    // clamped/wrong offset. Firing scroll events along the way is what lets the
    // scroll-based components (nav, Inplace) recompute their state.
    let rafId = 0;
    let attempts = 0;
    let stableFrames = 0;
    let lastHeight = document.documentElement.scrollHeight;
    const MAX_ATTEMPTS = 240;
    const MIN_ATTEMPTS = 24; // outlasts the page exit transition

    const apply = () => {
      window.scrollTo({ top: target, left: 0, behavior: 'instant' });
      document.documentElement.scrollTop = target;
      document.body.scrollTop = target;

      const h = document.documentElement.scrollHeight;
      if (h !== lastHeight) {
        lastHeight = h;
        stableFrames = 0;
      } else {
        stableFrames += 1;
      }
      attempts += 1;

      const reachable = target === 0 || h >= target + window.innerHeight;
      const settled = attempts >= MIN_ATTEMPTS && stableFrames >= 6 && reachable;

      if (!settled && attempts < MAX_ATTEMPTS) {
        rafId = requestAnimationFrame(apply);
      }
    };

    apply();
    rafId = requestAnimationFrame(apply);

    return () => cancelAnimationFrame(rafId);
  }, [pathname, key, navigationType]);

  return null;
}

// ============================================================
// NON-BLOCKING USER PROVIDER (fetches profile in background)
// ============================================================
function RootUserProvider({ children }: { children: React.ReactNode }) {
  const { userProfile, isLoading, error, refetchUserProfile } = useUserProfile();

  return (
    <UserProvider value={{ userProfile, isLoading, error, refetchUserProfile }}>
      {children}
    </UserProvider>
  );
}

// ============================================================
// PROTECTED ROUTE WRAPPER (shows loader until profile ready)
// ============================================================
function ProtectedRouteWithProfile({ children }: { children: React.ReactNode }) {
  const { isLoading } = useUser();

  if (isLoading) {
    return <Loader />;
  }

  return <ProtectedRoute>{children}</ProtectedRoute>;
}

function AppContent() {
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const location = useLocation();

  //capture affilate auid
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const auid = params.get('auid');
    if (auid) {
      localStorage.setItem(
        'affliateAttribution',
        JSON.stringify({
          id: auid,
          date: new Date().toISOString()
        })
      );
    }
  }, [])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        setIsSearchModalOpen(true);
        window.track('dashboard_searched');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <RootUserProvider>
      <ScrollToTop />

      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname.startsWith('/dashboard') ? '/dashboard' : location.pathname.startsWith('/docs') ? '/docs' : location.pathname}>
          {/* ========== PUBLIC ROUTES (render immediately, no dashboard code) ========== */}
          <Route path="/" element={<MainLayout />}>
            <Route index element={<AnimatedPage><HomePage /></AnimatedPage>} />
            <Route path="about" element={<AnimatedPage><AboutPage /></AnimatedPage>} />
            <Route path="products" element={<AnimatedPage><ProductsPage /></AnimatedPage>} />

            <Route path="developers" element={<AnimatedPage><DevelopersPage /></AnimatedPage>} />
            <Route path="developers/libraries" element={<AnimatedPage><LibrariesPage /></AnimatedPage>} />

            <Route path="contact" element={<AnimatedPage><ContactPage /></AnimatedPage>} />
            <Route path="pricing" element={<AnimatedPage><PricingPage /></AnimatedPage>} />
            <Route path="support" element={<AnimatedPage><FaqPage /></AnimatedPage>} />
            <Route path="jobs" element={<AnimatedPage><JobsPage /></AnimatedPage>} />
            <Route path="privacy" element={<AnimatedPage><PrivacyPage /></AnimatedPage>} />
            <Route path="terms" element={<AnimatedPage><TermsPage /></AnimatedPage>} />
            <Route path="products/stocks" element={<AnimatedPage><StocksPage /></AnimatedPage>} />
            <Route path="products/disclosures" element={<AnimatedPage><DisclosuresPage /></AnimatedPage>} />
            <Route path="products/earnings" element={<AnimatedPage><EarningsPage /></AnimatedPage>} />
            <Route path="products/credit" element={<AnimatedPage><CreditPage /></AnimatedPage>} />
            <Route path="products/esg" element={<AnimatedPage><EsgPage /></AnimatedPage>} />
            <Route path="products/etfs" element={<AnimatedPage><EtfsPage /></AnimatedPage>} />
            <Route path="products/supply-chain" element={<AnimatedPage><SupplyChainPage /></AnimatedPage>} />
            <Route path="products/crypto" element={<AnimatedPage><CryptoPage /></AnimatedPage>} />
            <Route path="products/forex" element={<AnimatedPage><ForexPage /></AnimatedPage>} />
            <Route path="products/futures" element={<AnimatedPage><FuturesPage /></AnimatedPage>} />
            <Route path="products/economics" element={<AnimatedPage><EconomicsPage /></AnimatedPage>} />
            <Route path="products/indices" element={<AnimatedPage><IndicesPage /></AnimatedPage>} />
            <Route path="products/corporate-profiles" element={<AnimatedPage><CorporateProfilesPage /></AnimatedPage>} />
            <Route path="products/sentiment" element={<AnimatedPage><SentimentPage /></AnimatedPage>} />
            <Route path="products/webtraffic" element={<AnimatedPage><WebtrafficPage /></AnimatedPage>} />
            <Route path="products/news" element={<AnimatedPage><NewsPage /></AnimatedPage>} />
            <Route path="/unsubscribe" element={<AnimatedPage><UnsubscribePage /></AnimatedPage>} />

            <Route path="stocks" element={<AnimatedPage><StocksHubPage /></AnimatedPage>} />
            <Route path="stock/:ticker" element={<AnimatedPage><TickerReportPage /></AnimatedPage>} />
            <Route path="history" element={<Navigate to={`/stock/${(tickerReports[0]?.ticker || 'MSFT').toLowerCase()}`} replace />} />

            <Route path="blog" element={<AnimatedPage><BlogPage /></AnimatedPage>} />
            <Route path="blog/:slug" element={<AnimatedPage><BlogArticlePage /></AnimatedPage>} />
            <Route path="compare" element={<AnimatedPage><CompareIndexPage /></AnimatedPage>} />
            <Route path="compare/:slug" element={<AnimatedPage><ComparisonPage /></AnimatedPage>} />
            <Route path="sitemap" element={<AnimatedPage><SitemapPage /></AnimatedPage>} />
          </Route>

          <Route path="/" element={<PlainLayout />}>
            <Route path="/login" element={<AnimatedPage><LoginPage /></AnimatedPage>} />
            <Route path="/signup" element={<AnimatedPage><SignupPage /></AnimatedPage>} />
            <Route path="/verify-email" element={<AnimatedPage><VerifyEmailPage /></AnimatedPage>} />
            <Route path="/forgot-password" element={<AnimatedPage><ForgotPasswordPage /></AnimatedPage>} />
            <Route path="/reset-password" element={<AnimatedPage><ResetPasswordPage /></AnimatedPage>} />
          </Route>

          {/* ========== PROTECTED ROUTES (dashboard code loads only when visited) ========== */}

          <Route
            path="/dashboard"
            element={
              <ProtectedRouteWithProfile>
                <DashboardLayout openSearchModal={() => setIsSearchModalOpen(true)} />
              </ProtectedRouteWithProfile>
            }
          >
            <Route index element={<AnimatedPage> <General /> </AnimatedPage>} />
            <Route path="usage" element={<AnimatedPage> <Usage /> </AnimatedPage>} />
            <Route path="api-keys" element={<AnimatedPage> <ApiKeys /> </AnimatedPage>} />
            <Route path="billing" element={<AnimatedPage> <Billing /> </AnimatedPage>} />
            <Route path="service-health" element={<AnimatedPage> <ServiceHealth /> </AnimatedPage>} />
            <Route path="security" element={<AnimatedPage> <Security /> </AnimatedPage>} />
            <Route path="profile" element={<AnimatedPage> <Profile /> </AnimatedPage>} />
            <Route path="playground" element={<AnimatedPage> <Playground /> </AnimatedPage>} />
            <Route path="mcp" element={<AnimatedPage> <MCPConfig /> </AnimatedPage>} />
          </Route>

          <Route
            path="/docs"
            element={
              <DocsScrollProvider>
                <DashboardLayout openSearchModal={() => setIsSearchModalOpen(true)} />
              </DocsScrollProvider>
            }
          >
            <Route path="/docs/" element={ <AnimatedPage><DocPage /> </AnimatedPage>} />
            <Route path="*" element={ <AnimatedPage><DocPage /> </AnimatedPage>} />
          </Route>
        </Routes>
      </AnimatePresence>

      {isSearchModalOpen && (
        <SearchModal onClose={() => setIsSearchModalOpen(false)} />
      )}
    </RootUserProvider>
  );
}

export default AppContent;
